// @mui
import { Stack, Grid, Button } from '@mui/material';
// hooks
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';
import { SEO_TEMPLATE, downloadJson, toSeoPayload } from '../seoImportExport';
import SeoPageCard from './SeoPageCard';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import Iconify from 'src/components/Iconify';
import { UserTableToolbar } from 'src/sections/@dashboard/user/list';
import ConfirmationDialog from '../../shared/ConfirmationDialog';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { SeoPageModal } from './SeoPageModal';
import { useSeoPages } from '../useSeoPages';
import { SeoPage } from 'src/@types/seoPage';
import { Typography } from '@mui/material';

// ----------------------------------------------------------------------
const SortOptions = ['all', 'acs', 'desc'];

// ----------------------------------------------------------------------

export default function SeoPageList() {
  const {
    modalOpen,
    setModalOpen,
    confirmationOpen,
    setConfirmationOpen,
    filterName,
    filterRole,
    currentSeoPage,
    setCurrentSeoPage,
    filteredData,
    handleDeleteRow,
    handleEditRow,
    handleFilterName,
    getSeoPageList,
  } = useSeoPages();

  useEffect(() => {
    getSeoPageList().catch(console.error);
  }, []);

  const [confirmOpen, setConfirmOpen] = useState({ open: false, currentId: '' });

  const { enqueueSnackbar } = useSnackbar();
  const fileRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);

  const handleExport = () => {
    const data = (filteredData || []).map((s: SeoPage) => ({
      _id: s._id,
      title: s.title ?? '',
      title_ar: s.title_ar ?? '',
      description: s.description ?? '',
      description_ar: s.description_ar ?? '',
      keywords: s.keywords ?? [],
      keywords_ar: s.keywords_ar ?? [],
    }));
    downloadJson(data, 'kayfa-seo-pages.json');
  };

  const handleTemplate = () => downloadJson(SEO_TEMPLATE, 'kayfa-seo-template.json');

  const handleImportFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-importing the same file
    if (!file) return;
    setImporting(true);
    try {
      // Tolerate a BOM / whitespace and accept an array, an { items: [...] }
      // wrapper, or a single object.
      const text = (await file.text()).replace(/^﻿/, '').trim();
      let parsed: any;
      try {
        parsed = JSON.parse(text);
      } catch (pErr: any) {
        throw new Error(`Invalid JSON — ${pErr?.message || 'could not parse the file'}`);
      }
      const rows: any[] = Array.isArray(parsed)
        ? parsed
        : Array.isArray(parsed?.items)
        ? parsed.items
        : parsed && typeof parsed === 'object'
        ? [parsed]
        : [];
      if (rows.length === 0) throw new Error('No SEO pages found in the file.');
      let created = 0;
      let updated = 0;
      let failed = 0;
      // eslint-disable-next-line no-restricted-syntax
      for (const row of rows) {
        const payload = toSeoPayload(row);
        try {
          if (row._id) {
            // eslint-disable-next-line no-await-in-loop
            await axiosInstance.patch(APIS.SEO_PAGES.PATCH(row._id), payload);
            updated += 1;
          } else {
            // eslint-disable-next-line no-await-in-loop
            await axiosInstance.post(APIS.SEO_PAGES.ADD, payload);
            created += 1;
          }
        } catch {
          failed += 1;
        }
      }
      enqueueSnackbar(
        `Import complete — ${updated} updated, ${created} created${failed ? `, ${failed} failed` : ''}`,
        { variant: failed ? 'warning' : 'success' },
      );
      await getSeoPageList();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Import failed — invalid file.', { variant: 'error' });
    } finally {
      setImporting(false);
    }
  };

  return (
    <Stack>
      <HeaderBreadcrumbs
        heading="SeoPage List"
        links={[
          { name: 'Dashboard', href: PATH_DASHBOARD.root },
          { name: 'SeoPage List', href: PATH_DASHBOARD.seoPages },
        ]}
        action={
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
            <input
              ref={fileRef}
              type="file"
              accept=".json,application/json"
              hidden
              onChange={handleImportFile}
            />
            <Button
              color="inherit"
              startIcon={<Iconify icon="eva:file-text-outline" />}
              onClick={handleTemplate}
            >
              Template
            </Button>
            <Button
              color="inherit"
              startIcon={<Iconify icon="eva:download-outline" />}
              onClick={handleExport}
            >
              Export
            </Button>
            <Button
              color="inherit"
              startIcon={<Iconify icon="eva:upload-outline" />}
              onClick={() => fileRef.current?.click()}
              disabled={importing}
            >
              {importing ? 'Importing…' : 'Import'}
            </Button>
            <Button
              variant="contained"
              onClick={() => setModalOpen(true)}
              startIcon={<Iconify icon={'eva:plus-fill'} />}
            >
              New SeoPage
            </Button>
          </Stack>
        }
      />
      <UserTableToolbar
        small={true}
        placeholder="Search SeoPage..."
        filterName={filterName}
        filterRole={filterRole}
        onFilterName={handleFilterName}
        onFilterRole={() => {}}
        optionsRole={SortOptions}
      />
      <Grid container>
        {filteredData.map((seoPage: SeoPage, index: number) => (
          <Grid
            spacing={1}
            key={seoPage._id}
            item
            xs={12}
            sm={6}
            md={4}
            lg={4}
            rowSpacing={1}
            columnSpacing={1}
          >
            <SeoPageCard
              seoPage={seoPage}
              key={index}
              handleEditRow={() => handleEditRow(seoPage)}
              handleDeleteRow={() => {
                setConfirmOpen({ open: true, currentId: seoPage._id });
              }}
            />
          </Grid>
        ))}
        {filteredData.length === 0 && (
          <Stack width={'100%'} height="300px" alignItems={'center'} justifyContent="center">
            <Typography> No result for your search data</Typography>
          </Stack>
        )}
      </Grid>
      <SeoPageModal
        open={modalOpen}
        handleClose={() => {
          setModalOpen(false);
          getSeoPageList();
          setCurrentSeoPage(undefined);
        }}
        isEdit={currentSeoPage ? true : false}
        currentSeoPage={currentSeoPage}
      />
      <ConfirmationDialog
        headText={'Delete SeoPage'}
        text={'Are You Sure You Need To Delete This SeoPage'}
        isOpen={confirmOpen.open}
        onClose={() => {
          setConfirmOpen({ open: false, currentId: '' });
          getSeoPageList();
        }}
        confirmAction={() => {
          handleDeleteRow(confirmOpen.currentId);
        }}
        loading={false}
      />
    </Stack>
  );
}
